<script lang="ts" setup>
import { ref, provide, watchEffect, toRef, computed } from 'vue';

import FormAddButton from './FormAddButton.vue';
import EvaluationsManualTasksModal from './EvaluationsManualTasksModal.vue';
import EvaluationsManualTasksModalEdit from './EvaluationsManualTasksModalEdit.vue';
import { useFieldArray, useValidateField } from 'vee-validate';
import {
  EvaluationParameterDto,
  instanceOfEnumerationEvaluationParameterDto,
  NumericEvaluationParameterDto,
  ParameterCombinationTableDto,
} from '@/api/generator';
import EvaluationsManualTasksEnumTile from './EvaluationsManualTasksEnumTile.vue';
import { Sortable } from 'sortablejs-vue3';
import { SortableEvent, SortableOptions } from 'sortablejs';
import { FormMode } from '@/components/forms/types';
import EvaluationsQuantityTile from './EvaluationsQuantityTile.vue';
import { clone } from 'lodash';
import { useToggle } from '@vueuse/core';
import InlineValidationError from '@/components/forms/InlineValidationError/InlineValidationError.vue';

interface EvaluationsManualTasksProps {
  name: string; // evaluationScopes[{index = 0}]
  mode: FormMode;
  targetSystemId?: string;
  combinationTable?: ParameterCombinationTableDto;
}

const props = defineProps<EvaluationsManualTasksProps>();

const targetSysId = ref(props.targetSystemId);

watchEffect(() => {
  targetSysId.value = props.targetSystemId;
  provide('targetSysId', targetSysId);
});

const [createOverlayOpen, toggleCreateOverlayOpen] = useToggle(false);

const editOverlayOpen = ref(false);

function toggleModalHandler() {
  toggleCreateOverlayOpen();
}

const validate = useValidateField(toRef(() => props.name));

const validateParams = useValidateField(toRef(() => `${props.name}.evaluationParameters`));

const {
  fields: evaluationParameters,
  push,
  remove,
  move: moveEvaluationParameter,
  update: updateEvaluationParameter,
} = useFieldArray<EvaluationParameterDto>(toRef(() => `${props.name}.evaluationParameters`));

async function pushEvaluationParameter(param: EvaluationParameterDto) {
  push(param);
  await validate();
  await validateParams();
}

async function removeEvaluationParameter(index: number) {
  remove(index);
  await validate();
  await validateParams();
}

function sortingEndHandler(e: SortableEvent) {
  if (e.oldIndex !== undefined && e.newIndex !== undefined) {
    moveEvaluationParameter(e.oldIndex, e.newIndex);
  }
}

const editIndex = ref<number>();

const editNumericEvaluationParameterDto = ref<NumericEvaluationParameterDto | undefined>();

const editNumericEvaluationParameterDtoHandler = (
  dto: NumericEvaluationParameterDto,
  myIndex: number,
) => {
  editIndex.value = myIndex;
  editOverlayOpen.value = true;
  editNumericEvaluationParameterDto.value = dto;
};

const sortableOptions = computed<SortableOptions>(() => ({
  handle: '.handle',
  disabled: props.mode === 'details',
  scroll: true,
  scrollSensitivity: 300,
  forceFallback: true,
}));
</script>

<template>
  <FormAddButton
    v-if="mode !== 'details'"
    :label="'AddEvaluation'"
    absolutePositioning
    class="absoluteAddBttonPositioning"
    :disabled="!targetSystemId"
    @click="toggleModalHandler"
  />

  <InlineValidationError
    :name="`${name}.evaluationParameters`"
    ignoreTouched
    class="inline-error dense mb-4"
  />

  <EvaluationsManualTasksModal
    v-model:overlayOpen="createOverlayOpen"
    @add="pushEvaluationParameter"
  />

  <EvaluationsManualTasksModalEdit
    v-model:overlayOpen="editOverlayOpen"
    :dtoToEdit="editNumericEvaluationParameterDto"
    @editEvaluationParameterDto="
      (paramDto: NumericEvaluationParameterDto) => {
        if (editIndex !== undefined && editIndex !== -1) {
          updateEvaluationParameter(editIndex, clone(paramDto));
        }
      }
    "
  />

  <v-expansion-panels multiple class="panels">
    <Sortable
      class="d-flex flex-column ga-2"
      :list="evaluationParameters"
      :options="sortableOptions"
      itemKey="key"
      @end="sortingEndHandler"
    >
      <template #item="{ element: myField, index }">
        <EvaluationsManualTasksEnumTile
          v-if="instanceOfEnumerationEvaluationParameterDto(myField.value)"
          :name="`${name}.evaluationParameters[${index}]`"
          @remove="removeEvaluationParameter(index)"
        />

        <EvaluationsQuantityTile
          v-else
          :name="`${name}.evaluationParameters[${index}]`"
          @remove="removeEvaluationParameter(index)"
          @edit="editNumericEvaluationParameterDtoHandler($event, index)"
        />
      </template>
    </Sortable>
  </v-expansion-panels>
</template>

<style scope lang="scss">
.absoluteAddBttonPositioning {
  position: absolute;
  right: 10px;
  top: 10px;
}
.panels {
  display: block !important;
}
</style>
